import { useEffect, useState } from 'react';
import { calendarSchoolYear, schoolYearOf, teachingWeeks, typicalCalendar } from '../../core/calendar';
import { draftAllowed, type Course } from '../../core/course';
import { courseMatchesPrefill, type CoursePrefill } from '../../core/standards-default';
import type { Calendar, IsoDate, PlanRecord } from '../../core/types';
import { call, courseLabel, toApiError, type GetToken, type PlanResponse, type PlanSummary } from '../api';
import { usePlanList } from '../usePlanList';
import { CalendarForm } from './CalendarForm';
import { CourseForm } from './CourseForm';
import { DraftProgress } from './DraftProgress';

type Step = 'course' | 'calendar' | 'start';
type Problem = { kind: 'standards' | 'quota' | 'limit' | 'draft' | 'other'; message: string };

const nextDraft = (at?: string) => at ? ` Your next draft is available ${new Date(at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}.` : '';

function problemFor(failure: unknown): Problem {
  const e = toApiError(failure);
  if (e.reason === 'standards_unavailable') return { kind: 'standards', message: 'We could not load the official standards for this course. You can still start blank and plan with named units.' };
  if (e.reason === 'quota_exceeded') return { kind: 'quota', message: `You have used all your drafts for the past seven days.${nextDraft(e.nextAvailableAt)} Start blank is not limited.` };
  if (e.reason === 'plan_limit') return { kind: 'limit', message: 'You already have five plans, the most you can keep. Delete one to start another.' };
  if (e.status === 502) return { kind: 'draft', message: 'The draft did not work this time. Try again, or start blank.' };
  return { kind: 'other', message: e.message };
}

/**
 * The plan a new plan's calendar is copied from (R2): the one updated last among those whose
 * calendar is for `schoolYear`, or null to use the typical dates.
 */
export function calendarSource<P extends Pick<PlanSummary, 'calendar' | 'updatedAt'>>(plans: readonly P[], schoolYear: number): P | null {
  return plans.filter(p => calendarSchoolYear(p.calendar) === schoolYear)
    .reduce<P | null>((latest, p) => (!latest || p.updatedAt > latest.updatedAt ? p : latest), null);
}

/** Course, then calendar, then Draft my year or Start blank (R3 to R5, R8, R9, R19). */
export function NewPlan({ getToken, today, onCreated, onCancel }: {
  getToken: GetToken; today: IsoDate; onCreated(planId: string, fresh: boolean): void; onCancel(): void;
}) {
  const [step, setStep] = useState<Step>('course');
  const [course, setCourse] = useState<Course | null>(null);
  const schoolYear = schoolYearOf(today);
  const [calendar, setCalendar] = useState<Calendar>(() => typicalCalendar(schoolYear));
  // The plan the calendar was copied from; undefined until the plan list has loaded or failed.
  const [copiedFrom, setCopiedFrom] = useState<PlanSummary | null | undefined>(undefined);
  // Bumped to remount the form when its starting dates are replaced.
  const [formKey, setFormKey] = useState(0);
  const [busy, setBusy] = useState<'draft' | 'blank' | null>(null);
  const [problem, setProblem] = useState<Problem | null>(null);

  // The shared default as a partial course; undefined until it has loaded or failed (R6, R11).
  const [prefill, setPrefill] = useState<CoursePrefill | undefined>(undefined);
  useEffect(() => {
    let live = true; const controller = new AbortController();
    void call<{ prefill?: unknown }>(getToken, '/api/standards-default', { signal: controller.signal })
      .then(answer => (answer.prefill && typeof answer.prefill === 'object' ? answer.prefill as CoursePrefill : {}), () => ({}))
      .then(next => { if (live) setPrefill(next); });
    return () => { live = false; controller.abort(); };
  }, [getToken]);

  const [plans] = usePlanList(getToken);
  useEffect(() => {
    if (plans === undefined) return;
    const source = plans ? calendarSource(plans, schoolYear) : null;
    if (source) setCalendar(source.calendar);
    setCopiedFrom(source);
  }, [plans, schoolYear]);

  const switchToTypical = () => { setCalendar(typicalCalendar(schoolYear)); setCopiedFrom(null); setFormKey(k => k + 1); };

  async function create(kind: 'draft' | 'blank') {
    if (!course || busy) return;
    setBusy(kind); setProblem(null);
    try {
      const body = JSON.stringify({ course, calendar, today, calendarCopied: !!copiedFrom, matchedPrefill: courseMatchesPrefill(prefill ?? {}, course) });
      const created = await call<{ plan: PlanRecord } | PlanResponse>(getToken, kind === 'draft' ? '/api/plans/draft' : '/api/plans', { method: 'POST', body });
      onCreated(created.plan.id, kind === 'draft');
    } catch (failure) {
      setProblem(problemFor(failure)); setBusy(null);
    }
  }

  const weeks = teachingWeeks(calendar).length;
  const canDraft = course !== null && draftAllowed(course.grade) && problem?.kind !== 'standards';
  return <section className="setup-card" aria-labelledby="new-plan-title">
    <div className="eyebrow">NEW PLAN · STEP {step === 'course' ? 1 : step === 'calendar' ? 2 : 3} OF 3</div>
    {step === 'course' && <>
      <h1 id="new-plan-title" className="setup-title">What are you teaching?</h1>
      {/* Held until the default answers, so a late one never replaces a field already picked.
          Back from the calendar, the course chosen wins over the default. */}
      {course === null && prefill === undefined ? <>
        <p className="notice" role="status">Loading your course…</p>
        <div className="form-footer"><button className="text-button" type="button" onClick={onCancel}>Cancel</button></div>
      </> : <CourseForm initial={course ?? prefill} onCancel={onCancel} onSubmit={c => { setCourse(c); setProblem(null); setStep('calendar'); }} />}
    </>}
    {step === 'calendar' && <>
      <h1 id="new-plan-title" className="setup-title">Your school calendar</h1>
      {copiedFrom === undefined ? <>
        <p className="notice" role="status">Loading your calendar…</p>
        <div className="form-footer"><button className="text-button" type="button" onClick={() => setStep('course')}>Back</button></div>
      </> : <>
        {copiedFrom
          ? <p className="muted">These dates are copied from your {courseLabel(copiedFrom.course)} plan. Change any of them to match yours. <button className="text-button" type="button" onClick={switchToTypical}>Use typical dates</button></p>
          : <p className="muted">These are typical dates for this school year. Change any of them to match yours.</p>}
        <CalendarForm key={formKey} initial={calendar} submitLabel="Continue" onCancel={() => setStep('course')} onSubmit={c => { setCalendar(c); setStep('start'); }} />
      </>}
    </>}
    {step === 'start' && course && <>
      <h1 id="new-plan-title" className="setup-title">How do you want to start?</h1>
      <p className="muted">{courseLabel(course)} · {weeks} teaching weeks</p>
      {problem && <div className="notice inline-notice" role="alert"><p>{problem.message}</p></div>}
      <div className="start-options">
        {canDraft && <div className="start-option">
          <button className="primary" type="button" disabled={busy !== null || problem?.kind === 'quota' || problem?.kind === 'limit'} onClick={() => void create('draft')}>Draft my year</button>
          <p className="muted">We sort your course’s official standards into named units that fill your {weeks} teaching weeks. You can change all of it.</p>
        </div>}
        <div className="start-option">
          <button className={canDraft ? 'secondary' : 'primary'} type="button" disabled={busy !== null || problem?.kind === 'limit'} onClick={() => void create('blank')}>{busy === 'blank' ? 'Starting…' : 'Start blank'}</button>
          <p className="muted">No units yet. Every standard waits in “Not yet placed” for you to sort.</p>
        </div>
      </div>
      {!draftAllowed(course.grade) && <p className="muted">Draft my year is for grades K–8. High-school standards are published for the whole 9–12 band, so you start blank with all of them and set aside the ones not in your course.</p>}
      <div className="form-footer"><button className="text-button" type="button" disabled={busy !== null} onClick={() => setStep('calendar')}>Back to the calendar</button></div>
      {busy === 'blank' && <p className="topic-list-status" role="status">Loading your course’s standards…</p>}
      {busy === 'draft' && <DraftProgress />}
    </>}
  </section>;
}
